import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
// Main App component with routing and authentication
import { Toaster } from 'react-hot-toast';
import './App.css';
import './styles/mobile-forum.css';
import VideoRecordingPage from './pages/VideoRecordingPage';
import EnhancedConfirmationPage from './pages/ConfirmationPage';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';

import ModernAuthPage from './pages/ModernAuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ReflectionQuestionsPage from './pages/ReflectionQuestionsPage';
import EmployersPage from './pages/EmployersPage';

import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';

import { AuthContextProvider } from './context/AuthContext';
import { useAuthContext } from './hooks/useAuthContext';
import { ThemeProvider } from './context/ThemeContext';

import EmployerLandingPage from './pages/EmployerLandingPage';
import Profile from './pages/Profile';
import AddJobPage from './pages/AddJobPage';
import CompanyDetailPage from './components/features/dashboard/companies/CompanyDetailPage';
import NewLandingPage from './pages/NewLandingPage';
import WaitlistView from './pages/WaitlistView';
import OnboardingFlow from './pages/OnboardingFlow';
import OnboardingTestFlow from './pages/OnboardingTestFlow';
import NetworkPage from './pages/NetworkPage';
import JobsPage from './pages/JobsPage';
import UserProfilePage from './pages/UserProfilePage';
import ForumPage from './pages/ForumPage';
import QuestionDetailPage from './pages/QuestionDetailPage';
import QuestionCreatePage from './pages/QuestionCreatePage';
import { initializeAnalytics } from './services/analytics';
import MapTestPage from './components/features/map/MapTestPage';
import ResponsiveNavigation from './components/navigation/ResponsiveNavigation';
import NotFoundPage from './pages/NotFoundPage';
import FeedbackButton from './components/features/feedback/FeedbackButton';
import FeedbackRetryNotification from './components/features/feedback/FeedbackRetryNotification';
import { ExternalLinkWarningProvider } from './context/ExternalLinkWarningContext';
import { ForumContextProvider } from './context/ForumContext';
import { OnboardingWalkthroughProvider } from './context/OnboardingWalkthroughContext';
import WalkthroughModal from './components/features/onboarding/walkthrough/WalkthroughModal';
import MobileWalkthroughCarousel from './components/features/onboarding/walkthrough/MobileWalkthroughCarousel';
import walkthroughService from './services/onboardingWalkthroughService';
import { isMobileView } from './utils/walkthroughTabHighlighting';
import WalkthroughResetButton from './components/debug/WalkthroughResetButton';
import { usePageVisitTracking } from './hooks/usePageVisitTracking';
import healthCheckService from './services/healthCheck';
import { initializeLikeBackgroundProcessor } from './utils/likeBackgroundProcessor';

function AppRoutes() {
  // Move the hook inside a component that is wrapped by AuthContextProvider
  const { user, userProfile, authIsReady, profileLoading } = useAuthContext();
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  
  
  // ============================================================
  // USER ACCESS CONTROL LOGIC
  // ============================================================
  // User status determines access level:
  // - 'waitlist': Account exists and user is signed in, but restricted to /waitlist page only
  // - 'invited': Can complete onboarding and access full application
  // - 'active': Can complete onboarding and access full application  
  // - undefined/null: Defaults to 'active' for backwards compatibility
  // ============================================================
  
  // Check if user is on waitlist
  const isWaitlistUser = user && userProfile && userProfile.status === 'waitlist';
  
  // Check if user needs onboarding
  // For new signups: user exists, profile loaded, and onboardingComplete is not explicitly true
  // Waitlist users should NOT go through onboarding
  const needsOnboarding = user && userProfile && userProfile.onboardingComplete !== true && !isWaitlistUser;
  
  // Check current routes
  const isOnboarding = location.pathname === '/onboarding';
  const isWaitlistPage = location.pathname === '/waitlist';
  
  // Track page visits for notification system
  usePageVisitTracking();

  // Show navigation loading indicator when route changes
  useEffect(() => {
    setIsNavigating(true);
    // Use requestAnimationFrame for better performance and shorter delay
    let timer;
    const frame = requestAnimationFrame(() => {
      timer = setTimeout(() => {
        setIsNavigating(false);
      }, 50); // Reduced from 150ms to 50ms
    });
    return () => {
      cancelAnimationFrame(frame);
      if (timer) clearTimeout(timer);
    };
  }, [location]);

  // Simple scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // If auth isn't ready yet, don't render anything
  if (!authIsReady) {
    return null;
  }

  // If user is authenticated but we're still loading their profile, show loading
  if (user && profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Global navigation loading indicator */}
      {isNavigating && (
        <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-gradient-to-r from-purple-500 to-pink-500">
          <div className="h-full bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse" />
        </div>
      )}
      
      {/* Navigation - only shows for authenticated users, but not on waitlist page */}
      {user && userProfile && !isWaitlistPage && <ResponsiveNavigation />}
      
      {/* Feedback System - only show for authenticated users who have completed onboarding */}
      {user && !needsOnboarding && !isOnboarding && !isWaitlistPage && (
        <>
          {/* Desktop feedback button */}
          <div className="hidden md:block">
            <FeedbackButton />
          </div>
          
          {/* Mobile feedback button - top right */}
          <div className="block md:hidden">
            <FeedbackButton 
              variant="floating" 
              position="top-right" 
              icon="lightbulb"
              style="secondary"
            />
          </div>
          
          <FeedbackRetryNotification />
        </>
      )}

      {/* Main content area with proper layout adjustments */}
      <div className={user && userProfile && !isOnboarding && !isWaitlistPage ? "md:pl-64 pb-16 md:pb-0 transition-all duration-300" : "transition-all duration-300"}>
        <Routes>
          {/* Public routes - accessible to everyone */}
          <Route path="/" element={<NewLandingPage />} />
          <Route path="/new-landing" element={<NewLandingPage />} />
          <Route path="/employers" element={<EmployerLandingPage />} />
          <Route path="/employer-landing" element={<EmployersPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/confirmation" element={<EnhancedConfirmationPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/map-test" element={<MapTestPage />} />
          
          {/* Waitlist route - public page for waitlist users to check status */}
          <Route path="/waitlist" element={<WaitlistView />} />
          
          {/* Auth routes */}
          <Route path="/auth" element={
            user ? (
              profileLoading ? (
                <div className="min-h-screen bg-white flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
                </div>
              ) :
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <Navigate to="/network" />
            ) : <ModernAuthPage />
          } />
          <Route path="/login" element={
            user ? (
              profileLoading ? (
                <div className="min-h-screen bg-white flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
                </div>
              ) :
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <Navigate to="/network" />
            ) : <Navigate to="/auth" />
          } />
          <Route path="/signup" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <Navigate to="/network" />
            ) : <Navigate to="/auth?mode=signup" />
          } />
          
          {/* Protected routes - require authentication */}
          <Route path="/dashboard" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <Dashboard />
            ) : <Navigate to="/" />
          } />
          <Route path="/onboarding" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              <OnboardingFlow />
            ) : <Navigate to="/" />
          } />
          <Route path="/onboarding-test" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              <OnboardingTestFlow />
            ) : <Navigate to="/" />
          } />
          <Route path="/companies/:companyId" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <CompanyDetailPage />
            ) : <Navigate to="/" />
          } />
          <Route path="/profile" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> :
              <Profile />
            ) : <Navigate to="/" />
          } />
          <Route path="/network" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> :
              <NetworkPage />
            ) : <Navigate to="/" />
          } />
          <Route path="/network/:userId" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> :
              <UserProfilePage />
            ) : <Navigate to="/" />
          } />
          <Route path="/jobs" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <JobsPage />
            ) : <Navigate to="/" />
          } />
          <Route path="/forum" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <ForumContextProvider><ForumPage /></ForumContextProvider>
            ) : <Navigate to="/" />
          } />
          <Route path="/forum/create" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <ForumContextProvider><QuestionCreatePage /></ForumContextProvider>
            ) : <Navigate to="/" />
          } />
          <Route path="/forum/:questionId" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <ForumContextProvider><QuestionDetailPage /></ForumContextProvider>
            ) : <Navigate to="/" />
          } />
          <Route path="/add-job" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <AddJobPage />
            ) : <Navigate to="/" />
          } />
          <Route 
            path="/admin" 
            element={
              user ? (
                isWaitlistUser ? <Navigate to="/waitlist" /> :
                needsOnboarding ? <Navigate to="/onboarding" /> :
                ['grey@justburnt.com', 'arjan@justburnt.com', 'louie@justburnt.com'].includes(user.email) ? 
                  <AdminDashboard /> 
                  : 
                  <Navigate to="/network" />
              ) : 
              <Navigate to="/" />
            } 
          />
          <Route path="/video-recording/:questionId?" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <VideoRecordingPage />
            ) : <Navigate to="/" />
          } />
          <Route path="/questions" element={
            user ? (
              isWaitlistUser ? <Navigate to="/waitlist" /> :
              needsOnboarding ? <Navigate to="/onboarding" /> : 
              <ReflectionQuestionsPage />
            ) : <Navigate to="/" />
          } />
          
          {/* Catch-all route for 404 - must be last */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  // Check if we should show debug tools (local development and preview environments)
  const isDebugEnvironment = () => {
    // Show in local development
    if (process.env.NODE_ENV === 'development') return true;
    
    // Show in preview deployments (Vercel preview URLs contain "vercel.app")
    if (typeof window !== 'undefined' && window.location.hostname.includes('vercel.app')) return true;
    
    // Show on preview branch (burnt-preview.com or similar)
    if (typeof window !== 'undefined' && window.location.hostname.includes('preview')) return true;
    
    return false;
  };

  const showDebugTools = isDebugEnvironment();

  useEffect(() => {
    // Initialize analytics in the background - don't block the UI at all
    setTimeout(() => {
      initializeAnalytics().catch((error) => {
        console.warn('Analytics failed:', error);
      });
    }, 1000); // Small delay to ensure UI is loaded first

    // Initialize like background processor for retry logic
    setTimeout(() => {
      initializeLikeBackgroundProcessor();
    }, 1500); // Initialize after analytics

    // Perform health check on app load
    setTimeout(() => {
      healthCheckService.performHealthCheck().catch((error) => {
        console.warn('Health check error:', error);
      });
    }, 2000); // Run after analytics

    // Development/Preview tools - expose walkthrough service to window for testing
    if (showDebugTools) {
      window.walkthroughService = walkthroughService;
      console.log('🔧 Debug mode: Use window.walkthroughService.resetWalkthrough() to reset the walkthrough');
    }
  }, [showDebugTools]);

  // Track window size for responsive walkthrough rendering
  const [isMobile, setIsMobile] = React.useState(isMobileView());

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <AuthContextProvider>
      <ThemeProvider>
        <ExternalLinkWarningProvider>
          <div className="App">
            <BrowserRouter>
              <OnboardingWalkthroughProvider>
                <AppRoutes />
                {isMobile ? <MobileWalkthroughCarousel /> : <WalkthroughModal />}
                {/* Debug button - show in development and preview environments */}
                {showDebugTools && <WalkthroughResetButton />}
              </OnboardingWalkthroughProvider>
            </BrowserRouter>
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
              }}
            />
          </div>
        </ExternalLinkWarningProvider>
      </ThemeProvider>
    </AuthContextProvider>
  );
}

export default App;